import React from 'react';
import { backgroundColorOptions } from './theme/colors';

// Official Puck Hero Section component implementation
const HeroSectionComponent = ({ 
  title = 'This page was built with Puck',
  description = 'Puck is the self-hosted visual editor for React. Bring your own components and make site changes instantly, without a deploy.',
  buttons = [
    { label: 'Visit GitHub', href: 'https://github.com', variant: 'primary' },
    { label: 'Edit this page', href: '#', variant: 'secondary' }
  ],
  align = 'left',
  padding = '80px',
  backgroundColor = 'slate-50'
}) => {
  
  // Removed color resolution - now using Tailwind dark mode classes

  const getButtonVariantClass = (variant) => {
    switch (variant) {
      case 'primary': return 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white border-transparent';
      case 'secondary': return 'bg-white hover:bg-gray-50 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-900 dark:text-slate-100 border-gray-300 dark:border-slate-600';
      case 'outline': return 'bg-transparent hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400';
      case 'ghost': return 'bg-transparent hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 border-transparent';
      default: return 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white border-transparent';
    }
  };

  const getButtonSizeClass = () => {
    return 'px-6 py-3 text-sm font-medium rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500';
  };

  const isCentered = align === 'center';

  return (
    <div 
      className="w-full relative overflow-hidden bg-white dark:bg-slate-900"
      style={{ 
        paddingTop: padding, 
        paddingBottom: padding
      }}
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex w-full">
          {/* Left half container for left alignment */}
          {!isCentered && (
            <div className="w-1/2 pr-8">
              <div className="text-left">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-slate-100 mb-6 leading-tight">
                  {title}
                </h1>
                <p className="text-lg md:text-xl text-gray-600 dark:text-slate-300 mb-8 leading-relaxed">
                  {description}
                </p>
                
                {/* Buttons */}
                {buttons && buttons.length > 0 && (
                  <div className="flex flex-wrap gap-4 justify-start">
                    {buttons.map((button, index) => (
                      <a
                        key={index}
                        href={button.href || '#'}
                        target={button.target || '_self'}
                        rel={button.target === '_blank' ? 'noopener noreferrer' : undefined}
                        className={`${getButtonSizeClass()} ${getButtonVariantClass(button.variant)}`}
                      >
                        {button.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          
          {/* Center container for center alignment */}
          {isCentered && (
            <div className="w-full flex justify-center">
              <div className="max-w-4xl text-center">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-slate-100 mb-6 leading-tight">
                  {title}
                </h1>
                <p className="text-lg md:text-xl text-gray-600 dark:text-slate-300 mb-8 leading-relaxed">
                  {description}
                </p>
                
                {/* Buttons */}
                {buttons && buttons.length > 0 && (
                  <div className="flex flex-wrap gap-4 justify-center">
                    {buttons.map((button, index) => (
                      <a
                        key={index}
                        href={button.href || '#'}
                        target={button.target || '_self'}
                        rel={button.target === '_blank' ? 'noopener noreferrer' : undefined}
                        className={`${getButtonSizeClass()} ${getButtonVariantClass(button.variant)}`}
                      >
                        {button.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const HeroSection = {
  fields: {
    title: {
      type: 'text',
      label: 'Titel'
    },
    description: {
      type: 'textarea',
      label: 'Beschreibung'
    },
    buttons: {
      type: 'array',
      label: 'Buttons',
      arrayFields: {
        label: {
          type: 'text',
          label: 'Button Text'
        },
        href: {
          type: 'text',
          label: 'Button URL'
        },
        variant: {
          type: 'select',
          label: 'Button Stil',
          options: [
            { label: 'Primär', value: 'primary' },
            { label: 'Sekundär', value: 'secondary' },
            { label: 'Umriss', value: 'outline' },
            { label: 'Transparent', value: 'ghost' }
          ]
        },
        target: {
          type: 'select',
          label: 'Link Ziel',
          options: [
            { label: 'Gleiches Fenster', value: '_self' },
            { label: 'Neues Fenster', value: '_blank' }
          ]
        }
      },
      getItemSummary: (item) => item.label || 'Button'
    },
    align: {
      type: 'select',
      label: 'Ausrichtung',
      options: [
        { label: 'Links', value: 'left' },
        { label: 'Zentriert', value: 'center' }
      ]
    },
    padding: {
      type: 'text',
      label: 'Padding',
      placeholder: '80px'
    },
    backgroundColor: {
      type: 'select',
      label: 'Hintergrundfarbe',
      options: backgroundColorOptions()
    }
  },
  defaultProps: {
    title: 'This page was built with Puck',
    description: 'Puck is the self-hosted visual editor for React. Bring your own components and make site changes instantly, without a deploy.',
    buttons: [
      { label: 'Jetzt bewerben', href: '/apply/campaign-id', variant: 'primary', target: '_self' }
    ],
    align: 'left',
    padding: '80px',
    backgroundColor: 'white'
  },
  render: HeroSectionComponent
};
